// The Files list in the Assistant panel (NEXUS-763): what the Assistant saved with write_file, open or download.
import { useEffect, useState } from 'react';
import { ErrorNote } from '../app/ui';
import { callFn } from '../lib/api';
import { ageLabel } from '../lib/format';
import { htmlDocument, sanitizeSvg } from '../lib/rich';
import { shareFiles, svgShareFiles, textFile } from '../lib/share';
import type { AssistantFile } from '../lib/types';
import { downloadText, Prose } from './Rich';

const ext = (name: string): string => (name.split('.').pop() ?? '').toLowerCase();

function Viewer({ file }: { file: AssistantFile & { content: string } }) {
  const e = ext(file.name);
  if (e === 'md') return <Prose markdown={file.content} />;
  if (e === 'html') return <iframe className="rich-frame full" sandbox="" referrerPolicy="no-referrer" srcDoc={htmlDocument(file.content)} title={file.name} />;
  if (e === 'svg') {
    const svg = sanitizeSvg(file.content);
    return svg ? <div className="rich-svg full" dangerouslySetInnerHTML={{ __html: svg }} /> : <div className="muted">Not a drawable SVG.</div>;
  }
  if (e === 'json') {
    let pretty = file.content;
    try { pretty = JSON.stringify(JSON.parse(file.content), null, 2); } catch { /* show as stored */ }
    return <pre className="file-pre">{pretty}</pre>;
  }
  return <pre className="file-pre">{file.content}</pre>;
}

export function FilesPanel({ reloadKey }: { reloadKey: number }) {
  const [files, setFiles] = useState<AssistantFile[] | null>(null);
  const [open, setOpen] = useState<(AssistantFile & { content: string }) | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    callFn<{ files: AssistantFile[] }>('list_files').then((r) => { if (live) setFiles(r.files); },
      (e) => { if (live) setError(e instanceof Error ? e.message : String(e)); });
    return () => { live = false; };
  }, [reloadKey]);

  async function fetchFile(f: AssistantFile): Promise<(AssistantFile & { content: string }) | null> {
    setError(null);
    try { return (await callFn<{ file: AssistantFile & { content: string } }>('get_file', { id: f.id })).file; }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); return null; }
  }

  async function share(x: AssistantFile & { content: string }) {
    try {
      const svg = ext(x.name) === 'svg' ? sanitizeSvg(x.content) : null;
      const r = await shareFiles(svg ? await svgShareFiles(x.name, svg) : [textFile(x.name, x.mime, x.content)], x.name);
      setError(r === 'downloaded' ? 'Downloaded (sharing is not available in this browser)' : null);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  }

  return (
    <div className="files-panel card">
      <h3 className="section-title">Files{files ? <span className="count">{files.length}</span> : null}</h3>
      <ErrorNote text={error} />
      {files && !files.length ? <div className="muted small">Nothing saved yet. Ask the Assistant to write a report, a CSV or a diagram to a file.</div> : null}
      <ul className="file-list">
        {(files ?? []).map((f) => (
          <li key={f.id}>
            <button type="button" className="link-btn" onClick={() => void fetchFile(f).then((x) => x && setOpen(x))}>{f.name}</button>
            <span className="muted small">{Math.max(1, Math.round(f.size / 1024))} KB · {ageLabel(f.createdAt)}</span>
            <button type="button" className="btn small" onClick={() => void fetchFile(f).then((x) => x && downloadText(x.name, x.mime, x.content))}>Download</button>
            <button type="button" className="btn small" onClick={() => void fetchFile(f).then((x) => x && share(x))}>Share</button>
          </li>
        ))}
      </ul>
      {open ? (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label={open.name} onClick={() => setOpen(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="rich-head">
              <strong>{open.name}</strong><span className="spacer" />
              <button type="button" className="btn small" onClick={() => downloadText(open.name, open.mime, open.content)}>Download</button>
              <button type="button" className="btn small" onClick={() => void share(open)}>Share</button>
              <button type="button" className="btn small" onClick={() => setOpen(null)}>Close</button>
            </div>
            <Viewer file={open} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
